import { GlobalAppState } from '@model/state';
import { createSelector } from 'reselect';
import { BookingStatus, MyBookingDetails, MyBookingErrorDetails, MyBookingToken } from '@model/mmb';
import Cookies, { Cookie } from '@model/common/cookie/cookie';

/**
 * Basic selectors - these remain exactly the same as in the original version
 * RTK doesn't change how we select data from state, only how we manage it
 */

export const getBookingToken: (state: GlobalAppState) => MyBookingToken = (state: GlobalAppState) => state.mmb.token;

export const getBookingDetails: (state: GlobalAppState) => MyBookingDetails = (state: GlobalAppState) =>
  state.mmb.bookingDetails;

export const getIsMyBookingError: (state: GlobalAppState) => boolean = (state: GlobalAppState) => state.mmb.error;

export const getIsMyBookingPending: (state: GlobalAppState) => boolean = (state: GlobalAppState) => state.mmb.pending;

export const getBookingErrorDetails: (state: GlobalAppState) => MyBookingErrorDetails | undefined = (
  state: GlobalAppState
) => state.mmb.bookingError;

export const getIsMyBookingLocked: (state: GlobalAppState) => boolean = (state: GlobalAppState) => state.mmb.locked;

export const getIsMyBookingAgent: (state: GlobalAppState) => boolean = (state: GlobalAppState) => state.mmb.agent;

export const getIsBookingCancelled: (state: GlobalAppState) => boolean = (state: GlobalAppState) =>
  state.mmb.bookingDetails.status === BookingStatus.CANCELLED;

export const getIsBookingClosed: (state: GlobalAppState) => boolean = (state: GlobalAppState) =>
  state.mmb.bookingDetails.status === BookingStatus.CLOSED;

export const getIsBookingEnquiry: (state: GlobalAppState) => boolean = (state: GlobalAppState) =>
  state.mmb.bookingDetails.status === BookingStatus.ENQUIRY;

export const getBookingId: (state: GlobalAppState) => string = (state: GlobalAppState) => state.mmb.bookingId;

/**
 * Memoized selectors using createSelector - these also remain the same
 * The beauty of selectors is they're completely independent of how state is managed
 */

export const getHasAgentData = createSelector(getBookingDetails, (bookingDetails: MyBookingDetails) => {
  return !!new Cookies().get(Cookie.TRADE_AUTHORIZATION) && !!bookingDetails.bookingID;
});

export const getCanRenderMyBooking = createSelector(
  getBookingToken,
  getBookingDetails,
  getIsMyBookingError,
  getHasAgentData,
  (token: MyBookingToken, bookingDetails: MyBookingDetails, error: boolean, hasAgentData) =>
    (!!token?.loginToken && !!bookingDetails.bookingID && !error) || hasAgentData
);

/**
 * Additional selectors that can be helpful with RTK async thunks
 * These help components understand the loading states better
 */
export const getIsBookingLoading: (state: GlobalAppState) => boolean = (state: GlobalAppState) => state.mmb.loading;

export const getIsAnyOperationPending = createSelector(
  getIsBookingLoading,
  getIsMyBookingPending,
  (isLoading: boolean, isPending: boolean) => isLoading || isPending
);

export const getBookingErrorState = createSelector(
  getIsMyBookingError,
  getBookingErrorDetails,
  getIsMyBookingLocked,
  getIsMyBookingAgent,
  (hasError: boolean, errorDetails: MyBookingErrorDetails | undefined, isLocked: boolean, isAgent: boolean) => ({
    hasError,
    errorDetails,
    isLocked,
    isAgent,
    // Helper to determine what kind of error state we're in
    errorType: isLocked ? 'locked' : isAgent ? 'agent' : hasError ? 'general' : null
  })
);
